import { CommandBar, Selection, ConstrainMode, DetailsListLayoutMode, IColumn, ICommandBarItemProps, ICommandBarStyles, IIconProps, mergeStyleSets, ScrollablePane, ScrollbarVisibility, SelectionMode, ShimmeredDetailsList, Stack, Icon, StackItem, Persona, PersonaSize, IDetailsFooterProps, Label, IStackStyles, IDetailsRowProps } from '@fluentui/react';
import React from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { ContentManagerCopyMoveModeEnum } from '../../../src/taskpane/components/enums/ContentManagerCopyMoveModeEnum';
import { RootState } from '../../../src/taskpane/components/_v2/redux/storeMain';
import { IFileToUpload } from '../../../src/taskpane/interfaces/IFileToUpload';
import { resetContentManagerDirectory, setContentManagerExchangeProgress, setContentManagerMoveTo, setContentManagerSearchValue } from '../../../src/taskpane/redux/modules/contentManagerDirectory';
import { addUploadedFiles, setFiles } from '../../../src/taskpane/redux/modules/file';
import { LanguageContext } from '../../../utilities/LocalizationModule';
import { CMConfirmModal } from '../../Panels/CMConfirmModal';
import dayjs from 'dayjs';
import 'dayjs/locale/nl'; // import locale
import 'dayjs/locale/de';
import { logger } from '../../../utilities/logger';
import generalModule from '../../../utilities/GeneralModule';
import ExpanderWithSubtitle from '../../../src/taskpane/components/directoryComponents/ExpanderWithSubtitle';
import { Fileview } from '../../../src/taskpane/components/_v2/components/Explorer/Fileview/Fileview';
import { isMobile } from '../../../src/taskpane/styles/mobileStyles';
import { WebPanel } from '../../../src/taskpane/components/_v2/_shared/WebPanel/WebPanel';
import MultipleFilesSeleted from '../../../src/taskpane/components/panels/MultipleFilesSeleted';
import { getExtension, removeExtension } from '../../../src/taskpane/components/helpers/stringHelper';
import { ExchangePreview } from './ExchangePreview';
import { IExchangeMessages, getExchangeMessageAttachments, getExchangeMessages } from '../../../src/taskpane/services/ExchangeService';
import { displayHeight } from '../../../src/taskpane/config/constants';
import { selectedExchangeItemsStore } from '../../../src/taskpane/services/selectedExchangeItemsStore';
import { resetWorkingDirectories } from '../../../src/taskpane/redux/modules/workingDirectory';
import { DragAndDropHelper } from '../../../utilities/DragAndDropHelper';
import { toggleReload } from '../../../src/taskpane/redux/modules/reload';
import { menuItemIconStyles } from '../../../src/taskpane/config/controlStyles';
import { ArrowClockwise20Regular } from '../../../src/taskpane/components/_v2/components/SvgIcons/ArrowClockwise20Regular';
import { Dismiss20Regular } from '../../../src/taskpane/components/_v2/components/SvgIcons/Dismiss20Regular';
import { Save20Regular } from '../../../src/taskpane/components/_v2/components/SvgIcons/Save20Regular';
import { SaveMultiple20Regular } from '../../../src/taskpane/components/_v2/components/SvgIcons/SaveMultiple20Regular';
const searchIconSrc = require("../../../assets/empty_search.svg");

const classNames = mergeStyleSets({
  wrapper: {
    height: displayHeight,
    position: "relative",
  }
});

const commandBarStyle: Partial<ICommandBarStyles> = {
  root: {
    borderTop: '1px solid var(--db-border, rgb(237, 235, 233))',
    borderBottom: '1px solid var(--db-border, rgb(237, 235, 233))',
    paddingLeft: 0,
    paddingRight: 10,
    paddingBottom: 3,
    paddingTop: 3
  }
};

const noResultsStackStyles: Partial<IStackStyles> = {
  root: { width: "100%", height: "calc(100vh - 250px)", margin: 0, padding: 0, overflow: "auto", userSelect: "none" }
};

const saveSelected: IIconProps = { iconName: "Save" };
const saveFolder: IIconProps = { iconName: "SaveAll" };
const cancelIcon: IIconProps = { iconName: "Cancel" };

interface ExchangeContentProps { }

export const ExchangeContent: React.FC<ExchangeContentProps> = ({ }) => {
  const languageStrings = React.useContext(LanguageContext);

  const MAX_SELECTED_FILES = 50;

  const dispatch = useDispatch();

  const workingDirectories = useSelector((state: RootState) => state.workingDirectories?.workingDirectories);
  const exchangeFolderId = useSelector((state: RootState) => state.contentManagerDirectory?.exchange.selectedFolderId);
  const exchangeAccount = useSelector((state: RootState) => state.contentManagerDirectory?.exchange.selectedAccount);
  const moveTo = useSelector((state: RootState) => state.contentManagerDirectory.moveTo);
  const contentManagerSearchValue = useSelector((state: RootState) => state.contentManagerDirectory?.searchValue);
  const files = useSelector((state: RootState) => state.files.files);
  const currentDirectory = useSelector((state: RootState) => state.workingDirectories.workingDirectories.length > 0 ? state.workingDirectories.workingDirectories[state.workingDirectories.workingDirectories.length - 1] : null);
  const searchValue = useSelector((state: RootState) => state.search?.value)?.value;
  const autoSelectAttachments = useSelector((state: RootState) => state.user?.settings.autoSelectAttachments);
  const reloadToggle = useSelector((state: RootState) => state.reload.toggle);

  const [isLoading, setIsLoading] = React.useState(true);
  // generation stamps which first-page fetch this list belongs to, so late
  // next-page responses for a previous folder/sort/filter can be recognized.
  const [items, setItems] = React.useState<IExchangeMessages & { generation?: number }>({ messages: [], generation: 0 });
  const [showConfirmMoveToDialog, setShowConfirmMoveToDialog] = React.useState<boolean>(false);
  const [showMaxFilesSelectedDialog, setShowMaxFilesSelectedDialog] = React.useState<boolean>(false);
  const [selectedItems, setSelectedItems] = React.useState<any[]>([]);
  const [isFetchingAttachments, setIsFetchingAttachments] = React.useState(false);
  const [isMultipleFilesSelectedPanelOpen, setIsMultipleFilesSelectedPanelOpen] = React.useState(false);
  const [sortColumn, setSortColumn] = React.useState("receivedDateTime");
  const [sortAscDesc, setSortAscDesc] = React.useState("desc");

  // Bumped whenever a fresh first page is requested (folder/sort/filter change);
  // in-flight next-page responses from an older generation must be discarded.
  const fetchGeneration = React.useRef<number>(0);
  // Generation whose next page is currently in flight (null = none). Keyed by
  // generation rather than a boolean so a stale flight for a previous folder
  // cannot block the current folder's paging.
  const fetchingNextPageGeneration = React.useRef<number | null>(null);

  const _selection = React.useRef(new Selection({
    onSelectionChanged: () => _getSelectionDetails(),
    // The null row is the infinite-scroll trigger, not a message; letting select-all
    // include it inflates the count past MAX_SELECTED_FILES and feeds null into the save flow.
    canSelectItem: (item: any) => !!item
  }));

  const _getSelectionDetails = () => {
    setSelectedItems(_selection.current.getSelection());
  };

  React.useEffect(() => {
    let isActive: boolean = true;

    // Batch sequential state updates
    const handler = setTimeout(() => {
      if (exchangeFolderId) {
        const fetchEmails = async () => {
          fetchGeneration.current++;

          const loadingDebounce = setTimeout(() => {
            if (isActive) {
              setIsLoading(true);
            }
          }, 250);

          try {
            let filter: string;
            if (generalModule.isInConentMangerMode()) {
              filter = contentManagerSearchValue ? contentManagerSearchValue.value : "";
            } else {
              filter = searchValue ? searchValue.value : "";
            }

            const messagesResult = await getExchangeMessages(exchangeFolderId, filter, sortColumn, sortAscDesc, exchangeAccount);

            if (isActive) {
              setMessageItems(messagesResult);
            }
          } catch (error) {
            logger(error, "ExchangeDirectory.tsx (fetchEmails)");
          }
          finally {
            if (isActive) {
              setIsLoading(false);
            }
            clearTimeout(loadingDebounce);
          }
        }
        fetchEmails();
      }
    }, 50);

    return () => {
      isActive = false;
      clearTimeout(handler);
    };
  }, [exchangeFolderId, contentManagerSearchValue, searchValue, sortColumn, sortAscDesc, exchangeAccount, reloadToggle]);

  const setMessageItems = React.useCallback((messagesResult: IExchangeMessages, isFirstPage: boolean = true) => {
    if (Array.isArray(messagesResult?.messages)) {
      const messageItems = messagesResult.messages.map(message => {
        return {
          ...message,
          selected: false
        }
      });

      // The null row is the infinite-scroll trigger of the DetailsList; the non-content-
      // manager expander view has no paging and would render it as a broken item.
      if (messagesResult?.nextLink?.length > 0 && generalModule.isInConentMangerMode()) {
        messageItems.push(null);
      }

      if (isFirstPage) {
        setItems({ messages: messageItems, nextLink: messagesResult.nextLink, generation: fetchGeneration.current });
      }
      else {
        setItems(items => { return { messages: items.messages.filter(msg => msg).concat(messageItems), nextLink: messagesResult.nextLink, generation: items.generation } });
      }
    }
  }, []);

  const fetchNextPageEmails = React.useCallback(async () => {
    // Key everything to the generation stamped on the list being extended — reading
    // the live counter here instead would let a stale nextLink captured after a
    // folder switch (bump happens before the new first page lands) slip through.
    const generation = items.generation;
    if (generation !== fetchGeneration.current
      || fetchingNextPageGeneration.current === generation
      || !(items.nextLink?.length > 0)) {
      return;
    }

    fetchingNextPageGeneration.current = generation;
    try {
      const nextPage = await getExchangeMessages(null, null, null, null, null, items.nextLink);
      // Discard pages that arrive after the folder/sort/filter changed; appending
      // them would mix another folder's emails into the current list.
      if (generation === fetchGeneration.current) {
        setMessageItems(nextPage, false);
      }
    } catch (error) {
      // State is left untouched so the trigger row survives and retries on a
      // later render instead of pagination silently ending.
      logger(error, "ExchangeDirectory.tsx (fetchNextPageEmails)");
    } finally {
      if (fetchingNextPageGeneration.current === generation) {
        fetchingNextPageGeneration.current = null;
      }
    }
  }, [items, setMessageItems]);

  const selectItem = React.useCallback((item: any) => {
    try {
      const newItems = [...items.messages];

      const selectedFile = newItems.find((c) => c.id === item.id);

      if (selectedFile) {
        selectedFile.selected = selectedFile.selected ? !selectedFile.selected : true;
        if (selectedFile.selected) {
          addEmailWithAttachmentToRedux(selectedFile);
        }
        else {
          dispatch(setFiles(files.filter(x => x.graphMailItemId !== selectedFile.id)));
        }
      }

      setItems(items => { return { ...items, messages: newItems } });
    } catch (error) {
      logger(error, "ExchangeDirectory.tsx (selectItem)");
    }
  }, [items, files]);

  const addEmailWithAttachmentToRedux = async (selectedFile: any) => {
    let filesToUpload: IFileToUpload[] = [
      {
        id: selectedFile.id,
        ewsId: null,
        name: selectedFile.subject || languageStrings.NoSubject,
        extension: "eml",
        isAttachment: false,
        isSelected: true,
        isDuplicate: false,
        originalName: selectedFile.subject || languageStrings.NoSubject,
        lastModifiedDateTime: selectedFile.receivedDateTime,
        isGraphMailItem: true,
        graphMailItemId: selectedFile.id
      }
    ];

    if (selectedFile.hasAttachments) {
      const attachments = await getExchangeMessageAttachments(exchangeAccount, selectedFile.id);
      if (attachments?.length > 0) {
        for (let index = 0; index < attachments.length; index++) {
          let attachment = attachments[index];
          const attachmentToUpload: IFileToUpload = {
            id: attachment.id,
            ewsId: null,
            name: removeExtension(attachment.name),
            extension: getExtension(attachment.name),
            isAttachment: true,
            isSelected: autoSelectAttachments,
            isDuplicate: false,
            originalName: removeExtension(attachment.name),
            lastModifiedDateTime: attachment.lastModifiedDateTime,
            isGraphMailItem: true,
            graphMailItemId: selectedFile.id
          }

          filesToUpload.push(attachmentToUpload);
        }
      }
    }

    dispatch(addUploadedFiles(filesToUpload));
  }

  const _onColumnClick = React.useCallback((ev: React.MouseEvent<HTMLElement>, column: IColumn): void => {
    if (column.key === sortColumn) {
      sortAscDesc === "asc" ? setSortAscDesc("desc") : setSortAscDesc("asc")
    } else {
      setSortColumn(column.key);
      setSortAscDesc("desc");
    }
  }, [sortColumn, sortAscDesc]);

  const columns: IColumn[] = React.useMemo(() => {
    return [
      {
        key: 'sender/emailAddress/name',
        name: languageStrings.From,
        minWidth: 150,
        maxWidth: 250,
        isRowHeader: true,
        isResizable: true,
        isSorted: sortColumn === 'sender/emailAddress/name',
        isSortedDescending: sortAscDesc === 'desc',
        data: 'string',
        onColumnClick: _onColumnClick,
        onRender: (item: any) => (
          <Stack horizontal verticalAlign='center' tokens={{ childrenGap: 15 }}>
            {item.sender && <StackItem>
              <Persona
                text={item.sender.emailAddress.name}
                size={PersonaSize.size24}
                hidePersonaDetails={true}
              />
            </StackItem>}
            {item.sender && <StackItem grow>{item.sender.emailAddress.name}</StackItem>}
            {item.sender && item.hasAttachments && <StackItem style={{ textAlign: "right" }}><Icon iconName="Attach" /></StackItem>}
            {!item.sender && <StackItem grow><span style={{ color: "rgb(216, 59, 1)" }}>[{languageStrings.Draft}]</span></StackItem>}
          </Stack>
        ),
      },
      {
        key: 'subject',
        name: languageStrings.Subject,
        minWidth: 250,
        isRowHeader: true,
        isResizable: true,
        isSorted: sortColumn === 'subject',
        isSortedDescending: sortAscDesc === 'desc',
        data: 'string',
        onColumnClick: _onColumnClick,
        onRender: (item: any) => {
          return <Stack horizontal tokens={{ childrenGap: 10 }} verticalAlign='center'>
            {item.subject && <StackItem style={{ fontWeight: "500" }}>{item.subject}</StackItem>}
            {!item.subject && <StackItem style={{ fontWeight: "500" }}>({languageStrings.NoSubject})</StackItem>}
            <StackItem style={{ fontSize: "400", paddingRight: "15px", whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }} grow>{item.bodyPreview}</StackItem>
          </Stack>
        },
      },
      {
        key: 'receivedDateTime',
        name: languageStrings.Received,
        fieldName: 'displayName',
        minWidth: 150,
        maxWidth: 150,
        isRowHeader: true,
        isResizable: true,
        isSorted: sortColumn === 'receivedDateTime',
        isSortedDescending: sortAscDesc === 'desc',
        data: 'string',
        onColumnClick: _onColumnClick,
        onRender: (item: any) => (
          <span>{dayjs(item.receivedDateTime).format("L LT")}</span>
        ),
      },
    ];
  }, [sortColumn, sortAscDesc, languageStrings, _onColumnClick]);

  const clearSelection = React.useCallback(() => {
    _selection.current.setAllSelected(false);
    setSelectedItems(_selection.current.getSelection());
  }, []);

  const [commandItems, commandFarItems] = React.useMemo(() => {
    const mainItems: ICommandBarItemProps[] = [];
    const farItems: ICommandBarItemProps[] = [];

    if (moveTo.moveToEnabled && (moveTo.mode === ContentManagerCopyMoveModeEnum.CopySelectedEmails || moveTo.mode === ContentManagerCopyMoveModeEnum.CopyEntireFolderEmails)) {
      mainItems.push({
        key: "cancel",
        text: languageStrings.Cancel,
        // iconProps: cancelIcon,
        iconProps: {},
        onRenderIcon: () => <Dismiss20Regular className={menuItemIconStyles} />,
        onClick: () => {
          cancelMove();
        },
      });

      farItems.push(
        {
          key: "selectedFileCountMove",
          text: `${moveTo.mode === ContentManagerCopyMoveModeEnum.CopySelectedEmails ? `${selectedItems.length} ${languageStrings.Selected.toLowerCase()}` : `${languageStrings.EntireFolderSelected}`}`,
          disabled: true,
        }
      )
    } else {
      mainItems.push({
        key: "save",
        disabled: selectedItems?.length === 0 || isLoading,
        text: languageStrings.SaveSelected,
        // iconProps: saveSelected,
        iconProps: {},
        onRenderIcon: () => <Save20Regular className={menuItemIconStyles} />,
        onClick: () => {
          if (DragAndDropHelper.isDroppedModeActive) {
            DragAndDropHelper.reset();
          }

          if (selectedItems.length > MAX_SELECTED_FILES) {
            setShowMaxFilesSelectedDialog(true);
          } else {
            dispatch(setContentManagerMoveTo({
              mode: ContentManagerCopyMoveModeEnum.CopySelectedEmails,
              moveToEnabled: true,
              workingDirectories: workingDirectories,
              selectedFiles: [],
              siteUrl: "",
              sourceSiteUrl: "",
              sourceServerRelPath: "",
              busyMovingFiles: false,
              copyJobResults: [],
              isMoveComplete: false,
              isMoveCancelled: false,
              destinationDirectory: {},
              progressCountCompleted: 0,
              progressCountTotal: 0,
              error: ""
            }));

            setShowConfirmMoveToDialog(true);
          }
        },
      });

      mainItems.push(
        {
          key: "saveAll",
          disabled: isLoading || items.messages.length === 0,
          text: languageStrings.SaveEntireFolder,
          // iconProps: saveFolder,
          iconProps: {},
          onRenderIcon: () => <SaveMultiple20Regular className={menuItemIconStyles} />,
          onClick: () => {
            if (DragAndDropHelper.isDroppedModeActive) {
              DragAndDropHelper.reset();
            }

            dispatch(setContentManagerExchangeProgress(undefined));
            dispatch(setContentManagerMoveTo({
              mode: ContentManagerCopyMoveModeEnum.CopyEntireFolderEmails,
              moveToEnabled: true,
              workingDirectories: workingDirectories,
              selectedFiles: [],
              siteUrl: "",
              sourceSiteUrl: "",
              sourceServerRelPath: "",
              busyMovingFiles: false,
              copyJobResults: [],
              isMoveComplete: false,
              isMoveCancelled: false,
              destinationDirectory: {},
              progressCountCompleted: 0,
              progressCountTotal: 0,
              error: ""
            }));
            setShowConfirmMoveToDialog(true);
          },
        })

      if (selectedItems?.length > 0) {
        farItems.push(
          {
            key: "selectedFileCount",
            text: `${selectedItems.length} ${languageStrings.Selected.toLowerCase()}`,
            // iconProps: { iconName: "Clear" },
            iconProps: {},
            onRenderIcon: () => <Dismiss20Regular className={menuItemIconStyles} />,
            onClick: clearSelection
          }
        )
      }

      farItems.push(
        {
          key: "refresh",
          disabled: isLoading,
          // iconProps: { iconName: "Refresh" },
          iconProps: {},
          onRenderIcon: () => <ArrowClockwise20Regular className={menuItemIconStyles} />,
          iconOnly: true,
          tooltipHostProps: {
            content: <span>{languageStrings.Refresh}</span>,
          },
          onClick: () => { dispatch(toggleReload()); }
        }
      )
    }

    return [mainItems, farItems];
  }, [selectedItems, isLoading, moveTo, items, clearSelection]);

  const onConfirmMove = React.useCallback(async (): Promise<void> => {
    if (generalModule.isInConentMangerMode()) {
      // getSelectedExchangeEmails(selectedItems.map(x => x.id), exchangeAccount, exchangeFolderId);
      // Entire-folder copies must always take the multi-email (auto-set) path:
      // caching a single list-selected email here would make the metadata form
      // direct-map THAT email's values and stamp them onto every email in the
      // folder. An empty array keeps the auto-set branch active. Writing through
      // the store also invalidates any in-flight header load (generation bump).
      selectedExchangeItemsStore.set(
        moveTo.mode === ContentManagerCopyMoveModeEnum.CopySelectedEmails ? selectedItems : []
      );

      if (moveTo.mode === ContentManagerCopyMoveModeEnum.CopySelectedEmails) {
        setIsFetchingAttachments(true);
        try {
          const filesToUpload: { index: number, fileToUpload: IFileToUpload }[] = [];

          for (let i = 0; i < selectedItems.length; i += 4) {
            const selectedItemSlices = selectedItems.slice(i, i + 4);

            await Promise.all(selectedItemSlices.map(async (selectedItem, index) => {
              const fileToUpload: IFileToUpload = {
                id: selectedItem.id,
                ewsId: null,
                name: selectedItem.subject || languageStrings.NoSubject,
                extension: "eml",
                isAttachment: false,
                isSelected: true,
                isDuplicate: false,
                originalName: selectedItem.subject || languageStrings.NoSubject,
                lastModifiedDateTime: selectedItem.receivedDateTime,
                isGraphMailItem: true,
                graphMailItemId: selectedItem.id
              };

              filesToUpload.push({ index, fileToUpload });

              if (selectedItem.hasAttachments) {
                const attachments = await getExchangeMessageAttachments(exchangeAccount, selectedItem.id);
                if (attachments?.length > 0) {
                  for (let indexAtt = 0; indexAtt < attachments.length; indexAtt++) {
                    let attachment = attachments[indexAtt];
                    const attachmentToUpload: IFileToUpload = {
                      id: attachment.id,
                      ewsId: null,
                      name: removeExtension(attachment.name),
                      extension: getExtension(attachment.name),
                      isAttachment: true,
                      isSelected: autoSelectAttachments,
                      isDuplicate: false,
                      originalName: removeExtension(attachment.name),
                      lastModifiedDateTime: attachment.lastModifiedDateTime,
                      isGraphMailItem: true,
                      graphMailItemId: selectedItem.id
                    }

                    filesToUpload.push({ index: index + ((indexAtt + 1) / 100), fileToUpload: attachmentToUpload });
                  }
                }
              }
            }));
          }

          dispatch(addUploadedFiles(filesToUpload.sort((a, b) => a.index - b.index).map(f => f.fileToUpload)));
        } catch (error) {
          logger(error, "ExchangeContent.tsx (onComfirmMove)");
        }
        setIsFetchingAttachments(false);
      }
    } else {
      setIsMultipleFilesSelectedPanelOpen(false);
      dispatch(setContentManagerMoveTo({
        mode: ContentManagerCopyMoveModeEnum.CopySelectedEmails,
        moveToEnabled: true,
        workingDirectories: [],
        selectedFiles: [],
        siteUrl: "",
        sourceSiteUrl: "",
        sourceServerRelPath: "",
        busyMovingFiles: false,
        copyJobResults: [],
        isMoveComplete: false,
        isMoveCancelled: false,
        destinationDirectory: {},
        progressCountCompleted: 0,
        progressCountTotal: 0,
        error: ""
      }));
      dispatch(setFiles(files.map(file => {
        return {
          ...file,
          isSelected: !file.isGraphMailItem ? false : file.isSelected
        }
      })));
    }

    setShowConfirmMoveToDialog(false);
    dispatch(setContentManagerSearchValue(""));
    dispatch(resetWorkingDirectories());
    dispatch(resetContentManagerDirectory());
  }, [selectedItems, moveTo, exchangeAccount, files, autoSelectAttachments]);

  const cancelMove = React.useCallback(() => {
    if (generalModule.isInConentMangerMode()) {
      dispatch(setFiles([]));
    } else {
      dispatch(setFiles(files.filter(x => !x.isGraphMailItem).map(file => {
        return {
          ...file,
          isSelected: true
        }
      })));

      setItems(items => { return { ...items, messages: items.messages.map(msg => { return { ...msg, selected: false } }) } });
    }

    dispatch(setContentManagerMoveTo({
      moveToEnabled: false,
      workingDirectories: [],
      selectedFiles: [],
      siteUrl: "",
      sourceServerRelPath: "",
      progressCountCompleted: 0,
      progressCountTotal: 0,
      error: ""
    }));

    setShowConfirmMoveToDialog(false);
  }, [files]);

  const _getKey = React.useCallback((item: any, index?: number): string => {
    return item?.id;
  }, []);

  const _onRenderDetailsFooter = (detailsFooterProps: IDetailsFooterProps): JSX.Element => {
    return (
      <>
        {!isLoading && items.messages.length === 0 && <Stack verticalAlign="center" horizontalAlign="center" styles={noResultsStackStyles}>
          <StackItem align="center">
            <img src={searchIconSrc} />
          </StackItem>
          <StackItem align="center">
            <Label disabled>{languageStrings.NoResultsFound}</Label>
          </StackItem>
        </Stack>}
      </>
    );
  }

  const onRenderCustomPlaceholder = React.useCallback((rowProps: IDetailsRowProps, index?: number, defaultRender?: (props: IDetailsRowProps) => React.ReactNode) => {
    if (rowProps.item === null) {
      fetchNextPageEmails();
    }
    return defaultRender(rowProps);
  }, [fetchNextPageEmails]);

  return (
    <>
      {generalModule.isInConentMangerMode() && <>
        <CommandBar
          styles={commandBarStyle}
          items={commandItems}
          farItems={commandFarItems}
          ariaLabel="Use left and right arrow keys to navigate between commands"
        />
        <Stack horizontal>
          <Stack.Item grow>
            <div className={classNames.wrapper}>
              <ScrollablePane scrollbarVisibility={ScrollbarVisibility.auto}>
                <ShimmeredDetailsList
                  compact={false}
                  items={items.messages}
                  columns={columns}
                  selectionMode={SelectionMode.multiple}
                  constrainMode={ConstrainMode.unconstrained}
                  getKey={_getKey}
                  enableShimmer={isLoading}
                  layoutMode={DetailsListLayoutMode.justified}
                  isHeaderVisible={true}
                  selectionPreservedOnEmptyClick={true}
                  selection={_selection.current}
                  ariaLabelForSelectionColumn="Toggle selection"
                  ariaLabelForSelectAllCheckbox="Toggle selection for all items"
                  checkButtonAriaLabel="Row checkbox"
                  onRenderDetailsFooter={_onRenderDetailsFooter}
                  onRenderCustomPlaceholder={onRenderCustomPlaceholder}
                />
              </ScrollablePane>
            </div>
          </Stack.Item>
          {selectedItems?.length === 1 && <Stack.Item>
            <ExchangePreview mailboxId={exchangeAccount} messageId={selectedItems[0].id} initialWidth={650} minWidth={500} />
          </Stack.Item>}
        </Stack>
      </>}
      {!generalModule.isInConentMangerMode() &&
        <div>
          <ExpanderWithSubtitle
            items={items.messages.map((item, index) => (
              <div key={index}>
                <Fileview
                  exchangeItem={item}
                  isMobile={isMobile}
                  tenantEnablePII={false}
                  multiSelect={true}
                  selectExchangeItem={selectItem}
                />
              </div>
            ))}
            itemsSelected={items.messages.filter(x => x.selected).length}
            title={languageStrings.Emails}
            selectedItemsMoreClicked={() => setIsMultipleFilesSelectedPanelOpen(true)}
          />
        </div>
      }
      {isMultipleFilesSelectedPanelOpen && (
        <WebPanel
          isOpen={isMultipleFilesSelectedPanelOpen}
          closePanel={() => setIsMultipleFilesSelectedPanelOpen(false)}
          headerText={`${files.filter(x => x.isSelected && x.isGraphMailItem).length} ${files.filter(x => x.isSelected && x.isGraphMailItem).length === 1
            ? languageStrings.FileSelected.toLowerCase()
            : languageStrings.FilesSelected.toLowerCase()
            }`}
          styles={{ main: { overflow: "unset" } }}
        >
          <MultipleFilesSeleted

            files={files.filter(x => x.isSelected && x.isGraphMailItem)}
            isGraphQuery={false}
            currentDirectory={currentDirectory}
            workingDirectories={workingDirectories}
            openConfirmSave={() => {
              if (files.filter(x => x.isSelected && x.isGraphMailItem).length > MAX_SELECTED_FILES) {
                setShowMaxFilesSelectedDialog(true);
              } else {
                setShowConfirmMoveToDialog(true);
              }
            }}
            setIsMultipleFilesSelectedPanelOpen={setIsMultipleFilesSelectedPanelOpen}
          />
        </WebPanel>
      )}

      {showConfirmMoveToDialog && <CMConfirmModal
        title={languageStrings.SaveEmails}
        message={languageStrings.SaveEmailsConfirmBody}
        showDialog={showConfirmMoveToDialog}
        isBusy={isFetchingAttachments}
        closeDialog={cancelMove}
        closeText=""
        confirmText={isFetchingAttachments ? languageStrings.DownloadingAttachments : languageStrings.Ok}
        onConfirm={onConfirmMove}
      />}
      {showMaxFilesSelectedDialog && <CMConfirmModal
        title={languageStrings.LimitReached}
        message={languageStrings.MaximumEmailMessagesSelected.replace("{amount}", MAX_SELECTED_FILES.toString())}
        showDialog={showMaxFilesSelectedDialog}
        closeDialog={() => {
          setShowMaxFilesSelectedDialog(false);
        }}
        closeText=""
        confirmText={languageStrings.Ok}
        onConfirm={() => {
          setShowMaxFilesSelectedDialog(false);
        }}
      />}
    </>
  );
};